import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Search,
  TrendingUp,
  Target,
  Zap,
  Eye,
  Shield,
  Sparkles,
  BarChart3,
  Globe,
  Brain,
  Link2,
  Gauge,
  Clock,
  Mail,
  FileText,
  Star,
  Plus,
  Minus,
  Loader2,
  Code,
  Smartphone,
  Share2,
  ChevronDown,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import { runSeoAudit, submitLead } from "../lib/api";

/* ─── Constants ───────────────────────────────────────────── */
const INDUSTRY_OPTIONS = [
  "Ecommerce / Shopify",
  "Real Estate",
  "Salon / Spa / Beauty",
  "Healthcare / Dental",
  "Restaurant / Food",
  "Legal / Law Firm",
  "Finance / Accounting",
  "Home Services",
  "Education / Coaching",
  "SaaS / Tech",
  "Travel / Hospitality",
  "Other",
];

const CATEGORY_META = {
  technical: { label: "Technical", icon: Zap, color: "text-blue-600" },
  "on-page": { label: "On-Page SEO", icon: Target, color: "text-electric" },
  content: { label: "Content", icon: FileText, color: "text-emerald-600" },
  social: { label: "Social / OG", icon: Share2, color: "text-pink-600" },
  schema: { label: "Structured Data", icon: Code, color: "text-violet-600" },
  performance: { label: "Performance", icon: Gauge, color: "text-amber-600" },
};

const TRUST_STATS = [
  { value: "30+", label: "Live checks per audit" },
  { value: "<30s", label: "Instant browser audit" },
  { value: "Free", label: "No credit card required" },
  { value: "4.9/5", label: "Avg client rating" },
];

const FAQS = [
  {
    q: "Is this SEO audit really free?",
    a: "Yes — completely free. The instant audit runs 30+ checks live in your browser the moment you submit your URL. You can also leave your email to receive a written 12-page report and a 15-minute Loom walkthrough from a senior strategist within 24 hours.",
  },
  {
    q: "How accurate is the instant audit?",
    a: "The instant audit fetches your live page and runs deterministic checks against established SEO best practices — title tags, meta descriptions, schema, OG tags, image alt text, mobile viewport, sitemap, robots.txt, performance signals and more. It's the same checklist our senior strategists use as their starting point.",
  },
  {
    q: "What does the audit actually check?",
    a: "30+ checks across 6 categories: Technical SEO (HTTPS, response time, mobile, charset, sitemap, robots.txt), On-Page SEO (title, meta description, H1/H2, canonical, hreflang, internal links), Content (word count, image alt text), Social (Open Graph, Twitter cards), Structured Data (JSON-LD schema types), and Performance (script and stylesheet count).",
  },
  {
    q: "Do you store the URL I submit?",
    a: "We log the URL and overall score to improve our audit tool. We never share or sell your data, and we'll only email you if you opted into the strategist report. You can request deletion any time at hello@lumotis.com.",
  },
  {
    q: "What if my site can't be reached?",
    a: "Some sites block bots or require JavaScript to render content. If the audit fails, double-check your URL is publicly accessible and try again. If issues persist, email hello@lumotis.com and our team will run a manual audit for you within 24 hours.",
  },
  {
    q: "Can I get a more detailed audit?",
    a: "Yes. After your instant audit, submit your email and we'll deliver a hand-written 12-page report covering competitor benchmark, backlink profile, keyword opportunities, AI search visibility (GEO/AEO), and a prioritized fix roadmap — within 24 hours.",
  },
];

/* ─── FAQ Accordion ──────────────────────────────────────── */
function FAQAccordion({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <ul className="border-t border-zinc-100">
      {items.map((f, idx) => {
        const isOpen = open === idx;
        return (
          <li key={f.q} className="border-b border-zinc-100">
            <button
              onClick={() => setOpen(isOpen ? -1 : idx)}
              className="w-full py-6 flex items-center justify-between gap-6 text-left group"
              aria-expanded={isOpen}
              data-testid={`audit-faq-${idx}`}
            >
              <span className="font-display text-lg md:text-xl font-bold text-charcoal group-hover:text-electric transition-colors">
                {f.q}
              </span>
              <span
                className={`shrink-0 w-9 h-9 grid place-items-center rounded-xl border transition-colors ${
                  isOpen
                    ? "bg-electric border-electric text-white"
                    : "border-zinc-200 text-charcoal group-hover:border-electric"
                }`}
              >
                {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </span>
            </button>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="pb-6 pr-12"
              >
                <p className="text-zinc-600 leading-relaxed text-[15px]">{f.a}</p>
              </motion.div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ─── Score Ring (animated SVG) ──────────────────────────── */
function ScoreRing({ score, size = 200 }) {
  const radius = (size - 20) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;

  let color = "#4731BA"; // electric default
  if (score >= 75) color = "#10B981"; // green
  else if (score >= 50) color = "#F59E0B"; // amber
  else color = "#EF4444"; // red

  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#F1F5F9"
          strokeWidth="12"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="font-display text-5xl font-extrabold text-charcoal leading-none"
          >
            {score}
          </motion.p>
          <p className="text-xs uppercase tracking-widest text-zinc-400 mt-1">/ 100</p>
        </div>
      </div>
    </div>
  );
}

/* ─── Status icon helper ─────────────────────────────────── */
function StatusIcon({ status }) {
  if (status === "pass")
    return (
      <span className="w-7 h-7 grid place-items-center rounded-lg bg-emerald-50 text-emerald-600 flex-shrink-0">
        <CheckCircle2 className="w-4 h-4" />
      </span>
    );
  if (status === "warn")
    return (
      <span className="w-7 h-7 grid place-items-center rounded-lg bg-amber-50 text-amber-600 flex-shrink-0">
        <AlertTriangle className="w-4 h-4" />
      </span>
    );
  if (status === "fail")
    return (
      <span className="w-7 h-7 grid place-items-center rounded-lg bg-red-50 text-red-600 flex-shrink-0">
        <XCircle className="w-4 h-4" />
      </span>
    );
  return (
    <span className="w-7 h-7 grid place-items-center rounded-lg bg-zinc-100 text-zinc-500 flex-shrink-0">
      <Info className="w-4 h-4" />
    </span>
  );
}

/* ─── Live progress (during audit) ───────────────────────── */
function AuditProgress({ url }) {
  const steps = [
    "Fetching your page…",
    "Parsing HTML structure…",
    "Checking technical SEO…",
    "Auditing on-page elements…",
    "Reviewing content quality…",
    "Inspecting Open Graph & social tags…",
    "Analyzing structured data…",
    "Testing sitemap & robots.txt…",
    "Running Google PageSpeed Insights…",
    "Measuring Core Web Vitals (LCP, CLS, INP)…",
    "Calculating final score…",
  ];
  const [stepIdx, setStepIdx] = useState(0);
  useEffect(() => {
    // Slow down on the PSI step — it genuinely takes 15-25s
    const t = setInterval(() => {
      setStepIdx((i) => {
        if (i >= steps.length - 1) return i;
        // Linger on the PSI / Core Web Vitals steps
        return i + 1;
      });
    }, 1800);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="rounded-2xl border border-zinc-100 bg-white p-8 md:p-10 shadow-card-hover relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-electric via-electric-400 to-electric animate-pulse" />
      <div className="flex items-center gap-4 mb-6">
        <Loader2 className="w-10 h-10 text-electric animate-spin" />
        <div>
          <p className="text-overline text-electric mb-1">Running live audit…</p>
          <p className="font-display text-xl font-bold text-charcoal">
            {url}
          </p>
        </div>
      </div>
      <ul className="space-y-3">
        {steps.map((s, i) => (
          <li
            key={s}
            className={`flex items-center gap-3 text-sm transition-all ${
              i < stepIdx ? "text-emerald-600" : i === stepIdx ? "text-electric font-semibold" : "text-zinc-400"
            }`}
          >
            {i < stepIdx ? (
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            ) : i === stepIdx ? (
              <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
            ) : (
              <div className="w-4 h-4 rounded-full border-2 border-zinc-200 flex-shrink-0" />
            )}
            {s}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─── Results section ────────────────────────────────────── */
function AuditResults({ data, onReset, onSendReport }) {
  const [filter, setFilter] = useState("all");
  const [emailForm, setEmailForm] = useState({ email: "", name: "" });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const filtered = data.findings.filter((f) => {
    if (filter === "all") return true;
    if (filter === "issues") return f.status === "fail" || f.status === "warn";
    return f.category === filter;
  });

  const handleSendReport = async (e) => {
    e.preventDefault();
    if (!emailForm.email) {
      toast.error("Please enter your email.");
      return;
    }
    setEmailLoading(true);
    try {
      await onSendReport(emailForm);
      setEmailSent(true);
      toast.success("Detailed report on the way — check your inbox in 24h.");
    } catch (err) {
      toast.error("Couldn't send — try again or email hello@lumotis.com.");
    } finally {
      setEmailLoading(false);
    }
  };

  const grade = data.grade;
  const gradeColor =
    grade === "A" ? "text-emerald-600 bg-emerald-50 border-emerald-200" :
    grade === "B" ? "text-blue-600 bg-blue-50 border-blue-200" :
    grade === "C" ? "text-amber-600 bg-amber-50 border-amber-200" :
    "text-red-600 bg-red-50 border-red-200";

  return (
    <div className="space-y-8" data-testid="audit-results">
      {/* ── Score summary card ───────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl border border-zinc-100 bg-white shadow-card-hover overflow-hidden"
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 bg-zinc-50">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
            </div>
            <span className="ml-3 text-xs font-mono text-zinc-500 truncate max-w-md">
              {data.url}
            </span>
          </div>
          <button
            onClick={onReset}
            className="text-xs font-semibold text-electric hover:underline"
            data-testid="audit-reset"
          >
            Run new audit
          </button>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 p-8 md:p-10">
          {/* Left — Score ring */}
          <div className="lg:col-span-4 flex flex-col items-center text-center">
            <ScoreRing score={data.score} />
            <span className={`mt-5 px-4 py-1.5 rounded-full border text-sm font-bold uppercase tracking-widest ${gradeColor}`}>
              Grade {grade}
            </span>
            <p className="mt-3 text-sm text-zinc-500">
              {data.score >= 75 ? "Strong SEO foundation"
                : data.score >= 50 ? "Solid base — room to improve"
                : "Significant SEO gaps detected"}
            </p>
          </div>

          {/* Right — Summary stats + category breakdown */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Passed</span>
                </div>
                <p className="font-display text-2xl font-extrabold text-charcoal">{data.summary.pass}</p>
              </div>
              <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Warnings</span>
                </div>
                <p className="font-display text-2xl font-extrabold text-charcoal">{data.summary.warn}</p>
              </div>
              <div className="rounded-xl border border-red-100 bg-red-50/50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className="w-4 h-4 text-red-600" />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-red-700">Failures</span>
                </div>
                <p className="font-display text-2xl font-extrabold text-charcoal">{data.summary.fail}</p>
              </div>
              <div className="rounded-xl border border-zinc-100 bg-zinc-50/50 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Info className="w-4 h-4 text-zinc-500" />
                  <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-600">Info</span>
                </div>
                <p className="font-display text-2xl font-extrabold text-charcoal">{data.summary.info}</p>
              </div>
            </div>

            {/* Category breakdown */}
            <div className="space-y-2.5">
              {Object.entries(data.category_scores).map(([cat, score]) => {
                const meta = CATEGORY_META[cat] || { label: cat, icon: Info, color: "text-zinc-500" };
                const Icon = meta.icon;
                return (
                  <div key={cat} className="flex items-center gap-3">
                    <Icon className={`w-4 h-4 ${meta.color} flex-shrink-0`} />
                    <span className="text-sm font-semibold text-charcoal w-32">{meta.label}</span>
                    <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${score}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className={`h-full rounded-full ${
                          score >= 75 ? "bg-emerald-500" : score >= 50 ? "bg-amber-500" : "bg-red-500"
                        }`}
                      />
                    </div>
                    <span className="text-sm font-bold text-charcoal w-12 text-right">{score}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Filter bar ────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-full transition-colors ${
            filter === "all" ? "bg-charcoal text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-electric hover:text-electric"
          }`}
        >
          All Findings ({data.findings.length})
        </button>
        <button
          onClick={() => setFilter("issues")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-full transition-colors ${
            filter === "issues" ? "bg-charcoal text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-electric hover:text-electric"
          }`}
        >
          Issues Only ({data.summary.warn + data.summary.fail})
        </button>
        {Object.entries(CATEGORY_META).map(([cat, meta]) => {
          const count = data.findings.filter((f) => f.category === cat).length;
          if (!count) return null;
          return (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-full transition-colors ${
                filter === cat ? "bg-charcoal text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-electric hover:text-electric"
              }`}
            >
              {meta.label} ({count})
            </button>
          );
        })}
      </div>

      {/* ── Findings list ─────────────────────────────────── */}
      <div className="space-y-3">
        {filtered.map((f, idx) => {
          const meta = CATEGORY_META[f.category] || { label: f.category, icon: Info };
          return (
            <motion.div
              key={`${f.name}-${idx}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: idx * 0.03 }}
              className="rounded-2xl border border-zinc-100 bg-white p-5 md:p-6 shadow-card hover:shadow-card-hover transition-shadow"
              data-testid={`finding-${idx}`}
            >
              <div className="flex items-start gap-4">
                <StatusIcon status={f.status} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2 mb-1">
                    <h3 className="font-display font-bold text-charcoal text-base">
                      {f.name}
                    </h3>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                      · {meta.label}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-600 mb-2 leading-relaxed">{f.message}</p>
                  {f.value && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zinc-50 border border-zinc-100 text-xs font-mono text-zinc-600 break-all">
                      {f.value}
                    </div>
                  )}
                  {f.fix && (
                    <div className="mt-3 pl-3 border-l-2 border-electric/30 bg-electric/5 py-2 px-3 rounded-r-lg">
                      <p className="text-[10px] uppercase tracking-widest text-electric font-bold mb-1">How to fix</p>
                      <p className="text-sm text-zinc-700 leading-relaxed">{f.fix}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* ── Email report CTA ──────────────────────────────── */}
      <div className="rounded-2xl bg-charcoal text-white p-8 md:p-12 relative overflow-hidden shadow-glow-brand">
        <div className="absolute inset-0 bg-grid-pattern-dark opacity-[0.06]" />
        <div aria-hidden className="absolute -right-24 -top-24 w-[420px] h-[420px] glow-orb bg-electric/20" />
        <div className="relative">
          {!emailSent ? (
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <p className="text-overline text-electric mb-3">Want the full picture?</p>
                <h3 className="font-display text-2xl md:text-3xl font-extrabold leading-tight mb-3">
                  Get a 12-page hand-written audit with competitor benchmark, backlink profile and AI search visibility.
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  A senior strategist personally reviews your site, your top 3 competitors, and your AI search presence — then sends a custom 12-page PDF + 15-min Loom walkthrough within 24 hours.
                </p>
              </div>
              <form onSubmit={handleSendReport} className="lg:col-span-5 space-y-3">
                <input
                  type="text"
                  value={emailForm.name}
                  onChange={(e) => setEmailForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Your name"
                  className="w-full px-4 py-3 text-sm bg-white/10 border border-white/15 rounded-xl text-white placeholder:text-zinc-400 focus:outline-none focus:border-electric"
                  data-testid="report-name"
                />
                <input
                  type="email"
                  required
                  value={emailForm.email}
                  onChange={(e) => setEmailForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="you@business.com"
                  className="w-full px-4 py-3 text-sm bg-white/10 border border-white/15 rounded-xl text-white placeholder:text-zinc-400 focus:outline-none focus:border-electric"
                  data-testid="report-email"
                />
                <button
                  type="submit"
                  disabled={emailLoading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 transition-all shadow-lg shadow-electric/30 disabled:opacity-60"
                  data-testid="report-submit"
                >
                  {emailLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                  ) : (
                    <>Send Me the 12-Page Report <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-electric mx-auto mb-4" />
              <h3 className="font-display text-2xl font-extrabold mb-2">Report on the way!</h3>
              <p className="text-zinc-400">A senior strategist will email you the full audit within 24 hours.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN PAGE ──────────────────────────────────────────── */
export default function SeoAuditToolPage() {
  const [formData, setFormData] = useState({
    url: "",
    industry: "",
    goal: "",
  });
  const [phase, setPhase] = useState("form"); // form | loading | results | error
  const [auditData, setAuditData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const resultsRef = useRef(null);

  const updateField = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const normaliseUrl = (raw) => {
    if (!raw) return "";
    const trimmed = raw.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.url) {
      toast.error("Please enter your website URL.");
      return;
    }
    setPhase("loading");
    setErrorMsg("");
    const startedAt = Date.now();
    try {
      const data = await runSeoAudit({
        url: normaliseUrl(formData.url),
        industry: formData.industry,
        goal: formData.goal,
      });
      // ensure minimum 4s of loading animation for UX polish
      const elapsed = Date.now() - startedAt;
      if (elapsed < 4000) {
        await new Promise((r) => setTimeout(r, 4000 - elapsed));
      }
      setAuditData(data);
      setPhase("results");
      // Scroll to results once rendered
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Couldn't run the audit. Please check the URL and try again.";
      setErrorMsg(msg);
      setPhase("error");
      toast.error(msg);
    }
  };

  const handleReset = () => {
    setPhase("form");
    setAuditData(null);
    setErrorMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSendReport = async (emailForm) => {
    await submitLead({
      name: emailForm.name || "Audit user",
      email: emailForm.email,
      message: `Requested full 12-page report for: ${auditData?.url}. Instant score: ${auditData?.score}/100.`,
      source: "seo-audit-instant",
      audit_score: auditData?.score,
    });
  };

  return (
    <>
      <Helmet>
        <title>Free Live SEO Audit Tool — Instant Website Audit | Lumotis</title>
        <meta
          name="description"
          content="Run a free live SEO audit of any website in 30 seconds. 30+ checks across technical SEO, on-page, content, social, schema and performance. Get instant results plus an optional 12-page expert report."
        />
        <meta
          name="keywords"
          content="free SEO audit tool, instant SEO checker, website SEO audit, live SEO analysis, SEO report generator, AI search visibility audit, GEO audit"
        />
        <link rel="canonical" href="https://lumotis.com/seo-audit" />
        <meta property="og:title" content="Free Live SEO Audit Tool — Instant Website Audit | Lumotis" />
        <meta
          property="og:description"
          content="Run a free live SEO audit of any website in 30 seconds. 30+ checks across technical SEO, on-page, content, social, schema and performance."
        />
        <meta property="og:url" content="https://lumotis.com/seo-audit" />
        <meta property="og:type" content="website" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Lumotis Free SEO Audit Tool",
            description:
              "Instant live SEO audit of any website. 30+ checks across technical, on-page, content, social, schema and performance.",
            url: "https://lumotis.com/seo-audit",
            applicationCategory: "BusinessApplication",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            provider: { "@type": "Organization", name: "Lumotis", url: "https://lumotis.com" },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              reviewCount: "200",
              bestRating: "5",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      </Helmet>

      <Navbar />

      <main data-testid="seo-audit-page">

        {/* ══════════════════════════════════════ HERO + FORM */}
        <section
          id="top"
          className="relative pt-28 md:pt-36 pb-16 md:pb-20 bg-white overflow-hidden border-b border-zinc-100"
        >
          <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
          <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/8 via-transparent to-transparent pointer-events-none" />
          <div
            aria-hidden
            className="absolute -left-32 bottom-0 w-[480px] h-[480px] glow-orb bg-electric/10 pointer-events-none"
          />

          <div className="container relative">
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 mb-8"
            >
              <Link to="/" className="hover:text-electric transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-charcoal">Free SEO Audit</span>
            </nav>

            <div className="grid lg:grid-cols-12 gap-12 items-start">
              {/* Left — copy */}
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 border border-electric/25 bg-electric/8 rounded-full px-4 py-1.5 mb-6">
                  <Sparkles className="w-4 h-4 text-electric" />
                  <span className="text-overline text-electric">Live SEO Audit · 30 seconds</span>
                </div>

                <motion.h1
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="font-display text-4xl sm:text-5xl lg:text-[58px] font-extrabold leading-[1.04] tracking-tight text-balance text-charcoal"
                >
                  Run a Free Live SEO Audit of Your Website in{" "}
                  <span className="text-gradient">30 Seconds</span>
                </motion.h1>

                <p className="mt-6 text-lg text-zinc-500 leading-relaxed max-w-xl">
                  Drop your URL and we'll instantly run 30+ checks across technical SEO, on-page, content, Open Graph, structured data and performance — with prioritised fixes for every issue we find.
                </p>

                <ul className="mt-8 space-y-3 max-w-lg">
                  {[
                    "30+ live SEO checks against your actual page",
                    "Score breakdown by category (technical, on-page, content, social, schema, performance)",
                    "Specific fix instructions for every issue found",
                    "Optional: 12-page strategist report within 24h",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-electric flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-600 leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 flex items-center gap-6">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-electric text-electric" />
                    ))}
                  </div>
                  <p className="text-sm text-zinc-500">
                    <span className="font-bold text-charcoal">10,000+</span> audits delivered ·{" "}
                    <span className="font-bold text-charcoal">4.9/5</span> rating
                  </p>
                </div>
              </div>

              {/* Right — Form / Progress / Results placeholder */}
              <div className="lg:col-span-6">
                <AnimatePresence mode="wait">
                  {phase === "form" && (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.35 }}
                      className="rounded-2xl border border-zinc-100 bg-white p-8 md:p-9 shadow-card-hover relative overflow-hidden"
                    >
                      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-electric via-electric-400 to-electric" />

                      <p className="text-overline text-electric mb-3">Instant audit · No signup</p>
                      <h2 className="font-display text-2xl md:text-3xl font-extrabold text-charcoal leading-tight">
                        Enter your website URL
                      </h2>
                      <p className="mt-3 text-sm text-zinc-500 leading-relaxed">
                        We'll fetch your page live and run the full check suite in your browser.
                      </p>

                      <form onSubmit={handleSubmit} className="mt-7 space-y-4" data-testid="seo-audit-form">
                        <div>
                          <label className="block text-xs font-bold text-charcoal uppercase tracking-widest mb-2">
                            Website URL <span className="text-electric">*</span>
                          </label>
                          <div className="relative">
                            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                            <input
                              type="text"
                              required
                              value={formData.url}
                              onChange={(e) => updateField("url", e.target.value)}
                              placeholder="yourwebsite.com"
                              className="w-full pl-10 pr-4 py-3 text-sm border border-zinc-200 rounded-xl focus:outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-all"
                              data-testid="audit-url-input"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-charcoal uppercase tracking-widest mb-2">
                            Industry (optional)
                          </label>
                          <select
                            value={formData.industry}
                            onChange={(e) => updateField("industry", e.target.value)}
                            className="w-full px-4 py-3 text-sm border border-zinc-200 rounded-xl focus:outline-none focus:border-electric focus:ring-2 focus:ring-electric/20 transition-all bg-white"
                            data-testid="audit-industry-select"
                          >
                            <option value="">Select industry…</option>
                            {INDUSTRY_OPTIONS.map((i) => (
                              <option key={i} value={i}>{i}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-charcoal uppercase tracking-widest mb-2">
                            Primary goal (optional)
                          </label>
                          <div className="grid grid-cols-3 gap-2">
                            {["Traffic", "Leads", "Sales"].map((g) => (
                              <button
                                key={g}
                                type="button"
                                onClick={() => updateField("goal", g)}
                                className={`px-3 py-2 text-xs font-semibold rounded-xl border transition-all ${
                                  formData.goal === g
                                    ? "bg-electric text-white border-electric"
                                    : "border-zinc-200 text-zinc-600 hover:border-electric hover:text-electric"
                                }`}
                                data-testid={`audit-goal-${g.toLowerCase()}`}
                              >
                                {g}
                              </button>
                            ))}
                          </div>
                        </div>

                        <button
                          type="submit"
                          data-testid="audit-submit"
                          className="w-full inline-flex items-center justify-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 hover:-translate-y-0.5 transition-all shadow-lg shadow-electric/30"
                        >
                          Run Free SEO Audit <ArrowRight className="w-4 h-4" />
                        </button>

                        <p className="text-[11px] text-zinc-400 text-center leading-relaxed">
                          Free. No credit card. No spam. Audits typically take 15–25 seconds.
                        </p>
                      </form>
                    </motion.div>
                  )}

                  {phase === "loading" && (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <AuditProgress url={normaliseUrl(formData.url)} />
                    </motion.div>
                  )}

                  {phase === "error" && (
                    <motion.div
                      key="error"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center"
                    >
                      <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                      <h3 className="font-display text-xl font-bold text-charcoal mb-2">
                        Audit failed
                      </h3>
                      <p className="text-sm text-zinc-600 mb-6">{errorMsg}</p>
                      <button
                        onClick={handleReset}
                        className="inline-flex items-center gap-2 bg-electric text-white px-5 py-2.5 font-semibold rounded-xl hover:bg-electric-500 transition-colors"
                      >
                        Try Again <ArrowRight className="w-4 h-4" />
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
              {TRUST_STATS.map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl border border-zinc-100 bg-white p-5 md:p-6 shadow-card"
                >
                  <p className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-none">
                    {s.value}
                  </p>
                  <p className="mt-2 text-[11px] uppercase tracking-widest text-zinc-500 leading-snug">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ RESULTS */}
        {phase === "results" && auditData && (
          <section
            ref={resultsRef}
            className="py-16 md:py-20 bg-zinc-50 border-b border-zinc-100 scroll-mt-24"
            data-testid="audit-results-section"
          >
            <div className="container max-w-6xl">
              <div className="mb-10">
                <p className="text-overline text-electric mb-3">Your live audit results</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal leading-[1.05] text-balance">
                  Here's exactly what we found.
                </h2>
              </div>
              <AuditResults
                data={auditData}
                onReset={handleReset}
                onSendReport={handleSendReport}
              />
            </div>
          </section>
        )}

        {/* ══════════════════════════════════════ WHAT'S CHECKED */}
        {phase === "form" && (
          <section className="py-20 md:py-28 bg-white">
            <div className="container">
              <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                <div className="lg:col-span-8">
                  <p className="text-overline text-electric mb-3">01 — What we check</p>
                  <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                    30+ SEO checks across 6 categories — live, against your real page.
                  </h2>
                </div>
                <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                  Every audit fetches your live URL, parses the HTML, and runs the full check suite against current SEO best practices.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {[
                  { icon: Zap, title: "Technical SEO", desc: "HTTPS, response time, mobile viewport, charset, language tag, favicon, sitemap.xml and robots.txt accessibility." },
                  { icon: Target, title: "On-Page SEO", desc: "Title tag (length + content), meta description, H1/H2 structure, canonical tag, robots directive, hreflang, internal links." },
                  { icon: FileText, title: "Content Quality", desc: "Visible word count, total images, percentage of images with alt text — the foundation of ranking-worthy content." },
                  { icon: Share2, title: "Social Sharing (OG)", desc: "Open Graph tags (title, description, image, URL, type) and Twitter Card tags for rich previews on social platforms." },
                  { icon: Code, title: "Structured Data", desc: "JSON-LD schema detection and type identification (Organization, Article, FAQPage, BreadcrumbList, Product, etc.)." },
                  { icon: Gauge, title: "Performance Signals", desc: "Page HTML size, external script count, external stylesheet count — proxy indicators for page speed." },
                ].map((c, idx) => {
                  const Icon = c.icon;
                  return (
                    <motion.div
                      key={c.title}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: idx * 0.05 }}
                      className="group rounded-2xl border border-zinc-100 bg-white p-7 md:p-8 shadow-card hover:shadow-card-hover hover:border-electric/30 hover:-translate-y-1 transition-all"
                    >
                      <div className="flex items-center justify-between mb-5">
                        <span className="w-11 h-11 grid place-items-center rounded-xl bg-electric/8 border border-electric/15 group-hover:bg-electric group-hover:border-electric transition-colors">
                          <Icon className="w-5 h-5 text-electric group-hover:text-white transition-colors" strokeWidth={2.2} />
                        </span>
                        <span className="font-display font-extrabold text-xl text-electric">
                          0{idx + 1}
                        </span>
                      </div>
                      <h3 className="font-display text-xl font-bold text-charcoal mb-3 leading-snug">
                        {c.title}
                      </h3>
                      <p className="text-sm text-zinc-500 leading-relaxed">{c.desc}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ══════════════════════════════════════ FAQ */}
        {phase === "form" && (
          <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-100">
            <div className="container grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <p className="text-overline text-electric mb-3">02 — FAQ</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  Audit tool questions.
                </h2>
                <p className="mt-5 text-zinc-500 leading-relaxed">
                  Have a different question? Email{" "}
                  <a href="mailto:hello@lumotis.com" className="text-electric font-semibold hover:underline">
                    hello@lumotis.com
                  </a>{" "}
                  — a real human replies within 24h.
                </p>
                <a
                  href="#top"
                  className="mt-8 inline-flex items-center gap-2 bg-charcoal text-white px-6 py-3 font-semibold rounded-xl hover:bg-electric transition-colors"
                >
                  Run my audit <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <div className="lg:col-span-8">
                <FAQAccordion items={FAQS} />
              </div>
            </div>
          </section>
        )}

        <CTABanner />
      </main>

      <Footer />
    </>
  );
}
